'use client';

import Link from 'next/link';
import { withLocalePrefix } from '@/lib/localePath';
import type { Locale } from '@/lib/localePath';
import '@/styles/InfoPage.css';

type Props = { locale: Locale };

export default function PrivacyClient({ locale }: Props) {
    const t = (ja: string, en: string, es: string) => {
        if (locale === 'ja') return ja;
        if (locale === 'es') return es;
        return en;
    };

    return (
        <article className="info-page">
            <nav className="awards-breadcrumb">
                <Link href={withLocalePrefix(locale, '/')}>{t('ホーム', 'Home', 'Inicio')}</Link>
                {' › '}
                <span>{t('プライバシーポリシー', 'Privacy Policy', 'Politica de privacidad')}</span>
            </nav>
            <h1>{t('プライバシーポリシー', 'Privacy Policy', 'Politica de privacidad')}</h1>
            <p className="info-page-updated">
                {t('最終更新日: 2026-03-22', 'Last updated: 2026-03-22', 'Ultima actualizacion: 2026-03-22')}
            </p>

            <section>
                <h2>{t('取得する情報', 'Information Collected', 'Informacion recopilada')}</h2>
                <ul>
                    <li>{t('IPアドレス', 'IP address', 'Direccion IP')}</li>
                    <li>{t('ブラウザ情報', 'Browser information', 'Informacion del navegador')}</li>
                    <li>{t('Cookieおよびアクセスログ', 'Cookies and access logs', 'Cookies y registros de acceso')}</li>
                </ul>
            </section>

            <section>
                <h2>{t('利用目的', 'Purpose of Use', 'Finalidad de uso')}</h2>
                <ul>
                    <li>{t('サイト運営・改善', 'Site operation and improvement', 'Operacion y mejora del sitio')}</li>
                    <li>{t('セキュリティ対策', 'Security and abuse prevention', 'Seguridad y prevencion de abuso')}</li>
                    <li>{t('アクセス解析', 'Traffic analytics', 'Analisis de trafico')}</li>
                </ul>
            </section>

            <section>
                <h2>{t('Cookieの利用', 'Cookie Usage', 'Uso de cookies')}</h2>
                <p>
                    {t(
                        '本サイトでは、利便性向上と利用状況把握のためCookieを利用する場合があります。Cookieの無効化はブラウザ設定から行えますが、一部機能が利用しづらくなることがあります。',
                        'This site may use cookies to improve user experience and understand traffic. You can disable cookies in browser settings, though some functionality may be limited.',
                        'Este sitio puede usar cookies para mejorar la experiencia y analizar el uso. Puedes desactivarlas en el navegador, pero algunas funciones pueden verse limitadas.'
                    )}
                </p>
                <p>
                    {t(
                        '本サイトではGoogle AdSenseによる広告を掲載しています。GoogleはCookieを使用して、ユーザーの本サイトや他サイトへの過去のアクセス情報に基づいて広告を配信します。DoubleClick Cookieを使用することで、Googleおよびそのパートナーはユーザーへのパーソナライズ広告の表示が可能になります。',
                        'This site uses Google AdSense to display advertisements. Google uses cookies to serve ads based on your prior visits to this and other sites. The use of the DoubleClick cookie enables Google and its partners to serve ads based on your visits to this and other sites.',
                        'Este sitio usa Google AdSense para mostrar anuncios. Google utiliza cookies para mostrar anuncios basados en visitas previas a este y otros sitios. La cookie de DoubleClick permite a Google y sus socios mostrar anuncios personalizados.'
                    )}
                </p>
                <p>
                    {t(
                        'パーソナライズ広告を無効にするには、',
                        'You can opt out of personalized advertising by visiting ',
                        'Puede desactivar los anuncios personalizados visitando '
                    )}
                    <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer">
                        Google 広告設定
                    </a>
                    {t(
                        'をご覧ください。また、',
                        '. You can also opt out of third-party vendor cookies for personalized advertising by visiting ',
                        '. También puede desactivar cookies de terceros en '
                    )}
                    <a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer">
                        www.aboutads.info
                    </a>
                    {t(
                        'からサードパーティベンダーのCookieを無効にすることもできます。',
                        '.',
                        '.'
                    )}
                </p>
            </section>

            <section>
                <h2>{t('第三者サービス', 'Third-Party Services', 'Servicios de terceros')}</h2>
                <ul>
                    <li>{t('Cloudflare等のインフラサービス', 'Infrastructure services such as Cloudflare', 'Servicios de infraestructura como Cloudflare')}</li>
                    <li>{t('Google Analytics（アクセス解析）', 'Google Analytics (traffic analytics)', 'Google Analytics (análisis de tráfico)')}</li>
                    <li>
                        {t(
                            'Google AdSense（広告配信）— Googleのプライバシーポリシーは',
                            'Google AdSense (ad delivery) — Google\'s privacy policy is available at ',
                            'Google AdSense (publicidad) — La política de privacidad de Google está disponible en '
                        )}
                        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                            https://policies.google.com/privacy
                        </a>
                        {t('をご確認ください。', '', '')}
                    </li>
                </ul>
            </section>

            <section>
                <h2>{t('情報共有について', 'Data Sharing', 'Comparticion de datos')}</h2>
                <p>
                    {t(
                        '法令に基づく場合を除き、個人を特定できる情報を第三者へ販売・提供することはありません。',
                        'Except when required by law, personally identifiable information is not sold or provided to third parties.',
                        'Salvo que la ley lo exija, no vendemos ni entregamos a terceros informacion personal identificable.'
                    )}
                </p>
            </section>

            <section>
                <h2>{t('ポリシー変更', 'Policy Changes', 'Cambios en la politica')}</h2>
                <p>
                    {t(
                        '本ポリシーは必要に応じて改定される場合があります。重要な変更がある場合は本ページで告知します。',
                        'This policy may be revised when necessary. Important changes will be announced on this page.',
                        'Esta politica puede actualizarse cuando sea necesario. Los cambios importantes se anunciaran en esta pagina.'
                    )}
                </p>
            </section>
        </article>
    );
}
